import React, { useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { purchaseFlow } from '../../help/flows';
import { SUPPLIER_PAYMENT_STAGES } from '../../help/statusStages';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Typography,
} from '@mui/material';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import {
  SupplierPayment, SupplierPaymentStatus, SupplierPaymentEdit,
} from '../../interfaces/procurement/supplierPayment.interface';
import { ReverseConfirmDialog } from './ReverseConfirmDialog';
import { isCurrentUser } from '../../utils/currentUser';
import { helpWhy } from '../../help/why';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { EditHistoryPanel, EditHistoryRow } from './EditHistoryPanel';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { WithholdingService } from '../../services/withholding.service';
import { PaymentWithholding } from '../../interfaces/procurement/withholding.interface';
import { saveBlob } from '../../utils/saveBlob';
import AttachmentsPanel from '../common/AttachmentsPanel'; // W3-K

const STATUS_COLORS: Record<SupplierPaymentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  POSTED: 'success',
  REVERSED: 'error',
};

export const SupplierPaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [payment, setPayment] = useState<SupplierPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [editHistoryRows, setEditHistoryRows] = useState<EditHistoryRow[]>([]);
  const [withholdings, setWithholdings] = useState<PaymentWithholding[]>([]);
  const canManagePayment = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE);

  useEffect(() => {
    if (!id) return;
    SupplierPaymentService.getById(id)
      .then(p => {
        setPayment(p);
        if ((p.withheldAmount ?? 0) > 0) {
          WithholdingService.listForPayment(id)
            .then(w => setWithholdings(w ?? []))
            .catch(() => setWithholdings([]));
        }
        if ((p.editCount ?? 0) > 0) {
          SupplierPaymentService.editHistory(id)
            .then((edits: SupplierPaymentEdit[]) => {
              setEditHistoryRows(edits.map(e => ({
                editSeq: e.editSeq,
                editedAt: e.editedAt,
                editReason: e.editReason,
                summary: `amount ${formatMoney(e.prevAmount)} → ${formatMoney(p.amount)}`,
              })));
            })
            .catch(() => setEditHistoryRows([]));
        }
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier payment'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReverse = async () => {
    if (!payment) return;
    setReversing(true);
    try {
      const updated = await SupplierPaymentService.reverse(payment.id);
      setPayment(updated);
      toast.showSuccess('Supplier payment reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse payment');
    } finally {
      setReversing(false);
    }
  };

  const downloadCertificate = async (w: PaymentWithholding) => {
    try {
      saveBlob(await WithholdingService.downloadCertificate(w.id), `${w.certificateNumber}.pdf`);
    } catch (e) {
      toast.showApiError(e, 'Failed to download the certificate');
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!payment) return <Box sx={{ p: 3 }}><Typography>Supplier payment not found.</Typography></Box>;

  // B-22, mirrors SupplierPaymentService.assertReverserIsNotMaker (unconditional, no setting): the maker cannot reverse.
  const makerBlock = isCurrentUser(payment.createdBy) ? 'You created this payment, so a second person must reverse it (maker and checker must be different users).' : null;
  const canReverse = payment.status === 'POSTED' && canManagePayment;   // server: reverse = SUPPLIER_PAYMENT_MANAGE
  const canEdit = payment.status === 'POSTED' && canManagePayment && (payment.withheldAmount ?? 0) === 0;

  return (
    <Box sx={{ p: 3 }}>
      <FlowStrip data-help="procurement.payment.flow" stages={purchaseFlow({ current: 'payment', bill: { id: payment.supplierBillId, no: payment.supplierBillNumber }, payment: { no: payment.paymentNumber } })} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{payment.paymentNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button
              data-help="accounting.supplierPayment.edit"
              variant="outlined"
              onClick={() => navigate(`/procurement/supplier-payments/${payment.id}/edit`)}
            >
              Edit
            </Button>
          )}
          {canReverse && (
            <Button data-help="accounting.supplierPayment.reverse" {...helpWhy(makerBlock ? { kind: 'custom', message: makerBlock } : null)} disabled={!!makerBlock} variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Payment Number</Typography>
            <Typography>{payment.paymentNumber}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <StatusStagesChip data-help="procurement.payment.statusStages" label={humanizeEnum(payment.status)} status={payment.status} stages={SUPPLIER_PAYMENT_STAGES} color={STATUS_COLORS[payment.status]} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Supplier Bill</Typography>
            <Typography
              component="a"
              href={`/procurement/supplier-bills/${payment.supplierBillId}`}
              onClick={(ev: React.MouseEvent) => { ev.preventDefault(); navigate(`/procurement/supplier-bills/${payment.supplierBillId}`); }}
              sx={{ color: 'primary.main', cursor: 'pointer' }}
            >
              {payment.supplierBillNumber ?? 'View bill'}
            </Typography>
          </Box>
          {payment.supplierName && (
            <Box>
              <Typography variant="body2" color="text.secondary">Supplier</Typography>
              <Typography>{payment.supplierName}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Payment Date</Typography>
            <Typography>{formatDate(payment.paymentDate)}</Typography>
          </Box>
          {isForeignCurrency(payment.currencyCode) && payment.foreignAmount != null && payment.exchangeRate != null ? (
            <Box data-testid="fc-payment-amount">
              {/* G5a polish: the foreign amount carries its currency; the two BDT figures say which is which */}
              <Typography variant="body2" color="text.secondary">Amount ({payment.currencyCode})</Typography>
              <Typography>{formatFc(payment.currencyCode!, payment.foreignAmount)}</Typography>
              <Typography variant="body2">
                BDT equivalent at the payment rate {payment.exchangeRate}: BDT {formatMoney(payment.bdtAmount ?? payment.foreignAmount * payment.exchangeRate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bill carrying value cleared: BDT {formatMoney(payment.amount)}
              </Typography>
              {payment.fxDifference != null && payment.fxDifference !== 0 && (
                <Typography variant="body2" color={payment.fxDifference > 0 ? 'success.main' : 'error.main'}>
                  Realised FX {payment.fxDifference > 0 ? 'gain' : 'loss'}: BDT {formatMoney(Math.abs(payment.fxDifference))}
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary">Amount</Typography>
              <Typography>{formatMoney(payment.amount)}</Typography>
            </Box>
          )}
          {(payment.withheldAmount ?? 0) > 0 && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Withheld at source (VDS / TDS)</Typography>
                <Typography>{formatMoney(payment.withheldAmount)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Net cash paid</Typography>
                <Typography>{formatMoney(payment.amount - (payment.withheldAmount ?? 0))}</Typography>
              </Box>
            </>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Paid From Account</Typography>
            <Typography>{payment.paymentAccountName ?? '—'}</Typography>
          </Box>
          {payment.paymentMethod && (
            <Box>
              <Typography variant="body2" color="text.secondary">Payment Method</Typography>
              <Typography>{payment.paymentMethod}</Typography>
            </Box>
          )}
          {payment.reference && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reference</Typography>
              <Typography>{payment.reference}</Typography>
            </Box>
          )}
          {payment.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry</Typography>
              <Typography>{payment.journalEntryNumber ?? '—'}</Typography>
            </Box>
          )}
          {payment.status === 'REVERSED' && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed Date</Typography>
                <Typography>{payment.reversedDate ? formatDate(payment.reversedDate) : '-'}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {withholdings.length > 0 && (
        <Paper sx={{ p: 3, mt: 2 }} data-testid="payment-withholdings">
          <Typography variant="h6" sx={{ mb: 1 }}>Withholding at source</Typography>
          {withholdings.map(w => (
            <Box key={w.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', py: 0.5 }}>
              <Typography sx={{ minWidth: 40 }} fontWeight="bold">{w.type}</Typography>
              <Typography sx={{ flex: '1 1 200px' }}>
                {w.ruleCode} - {w.ratePct}% of {formatMoney(w.baseAmount)}
              </Typography>
              <Typography sx={{ minWidth: 110 }} align="right">{formatMoney(w.amount)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {w.status === 'VOIDED' ? `${w.certificateNumber} (void)` : w.certificateNumber}
                {w.challanNumber ? ` - challan ${w.challanNumber}` : ''}
              </Typography>
              {w.status === 'VOIDED' ? (
                <Chip size="small" color="error" variant="outlined" label="Void - payment reversed" data-testid={`withholding-void-${w.id}`} />
              ) : (
                <Button size="small" variant="outlined" onClick={() => downloadCertificate(w)}>
                  {w.type === 'VDS' ? 'Mushak 6.6 certificate' : 'TDS certificate'}
                </Button>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {(payment.editCount ?? 0) > 0 && (
        <EditHistoryPanel title="Edit history" rows={editHistoryRows} />
      )}

      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Supplier Payment"
        consequence="Cash and AP movements will be reversed. The associated bill will move back to unpaid or partially paid depending on remaining payments. If you recorded this payment yourself, a second person must reverse it."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />
      {id && <AttachmentsPanel entityType="SUPPLIER_PAYMENT" entityId={id} canManage={canManagePayment} />} {/* W3-K */}
    </Box>
  );
};

export default SupplierPaymentDetail;
